import { importProcessingFirestore } from "./processing-firestore-import";
import { loadModuleWithPreloadFallback } from "../../utils/vite-preload-error-recovery";
import { db } from "../../backend/utils/init-firebase";
import { addSentryDiagnosticBreadcrumb } from "../../utils/analytics/track-error";
import { createFileUploadCancellationError, FileUploadStage } from "./file-upload-client";
import { collection, doc, getDocFromServer, getDocsFromServer, orderBy, query } from "firebase/firestore";
import {
  GET_DATA_FROM_FILE_PROCESSING_STATUS_COLLECTION_NAME,
  TEXT_PAGES_FOR_PROCESSING_STATUS_COLLECTION_NAME,
} from "../../constants/GlobalTypesAndEnumsAndConstants";

export async function preloadProcessingResultReaders(): Promise<void> {
  await Promise.all([loadProcessingFirestore(), import("./processed-file-text"), import("./processing-task-wait")]);
}

let processingFirestore: Promise<typeof import("firebase/firestore/lite") | null> | undefined;

function isTransientProcessingTextReadError(error: unknown): boolean {
  if (!(error instanceof Error) || error.name !== "FirebaseError") return false;
  const code = (error as Error & { code?: string }).code;
  const isExplicitTransientError = code === "unavailable" || code === "deadline-exceeded" || code === "resource-exhausted";
  // Lite maps a dropped HTTP connection without a status to this exact error.
  // Other unknown errors may be malformed responses or application defects.
  const isDroppedConnection = code === "unknown" && error.message === "Request failed with error: undefined";
  return isExplicitTransientError || isDroppedConnection;
}

function loadProcessingFirestore() {
  return processingFirestore ??= loadModuleWithPreloadFallback(importProcessingFirestore, (error: unknown) => {
    // A failed dynamic import can remain rejected for this document's lifetime,
    // even after connectivity returns. The full SDK is already loaded for the
    // task listener: use its server reads when this optional chunk cannot load.
    // Do not hide module evaluation bugs or retry a rejected Firestore request.
    const isChunkLoadFailure = error instanceof Error && (error.name === "ChunkLoadError" ||
      /dynamically imported module|Importing a module script failed|Loading chunk|'text\/html' is not a valid JavaScript MIME type/i.test(error.message));
    if (isChunkLoadFailure) {
      return null;
    }
    throw error;
  });
}

// Firestore Lite uses independent REST requests with the same app/auth/rules.
// The full SDK's getDocFromServer can reuse the upload's stalled Listen channel.
// Load Lite only when reading processing results, outside the startup bundle.
async function processingReference(taskId: string) {
  const lite = await loadProcessingFirestore();
  if (!lite) return null;
  const reference = lite.doc(lite.getFirestore(db.app), GET_DATA_FROM_FILE_PROCESSING_STATUS_COLLECTION_NAME, taskId);
  return { lite, reference };
}

export async function readProcessingTask(taskId: string): Promise<Record<string, unknown> | null> {
  const processing = await processingReference(taskId);
  const snapshot = processing
    ? await processing.lite.getDoc(processing.reference)
    : await getDocFromServer(doc(db, GET_DATA_FROM_FILE_PROCESSING_STATUS_COLLECTION_NAME, taskId));
  return snapshot.exists() ? snapshot.data() : null;
}

export async function readProcessingTextDocuments(taskId: string, cancellationSignal?: AbortSignal): Promise<Record<string, unknown>[]> {
  const processing = await processingReference(taskId);
  for (let attempt = 1; ; attempt += 1) {
    if (cancellationSignal?.aborted) {
      throw createFileUploadCancellationError(FileUploadStage.ReadProcessedText, cancellationSignal.reason);
    }
    try {
      const snapshot = processing
        ? await processing.lite.getDocs(processing.lite.query(
          processing.lite.collection(processing.reference, TEXT_PAGES_FOR_PROCESSING_STATUS_COLLECTION_NAME),
          processing.lite.orderBy("pageIndex")
        ))
        : await getDocsFromServer(query(
          collection(doc(db, GET_DATA_FROM_FILE_PROCESSING_STATUS_COLLECTION_NAME, taskId), TEXT_PAGES_FOR_PROCESSING_STATUS_COLLECTION_NAME),
          orderBy("pageIndex")
        ));
      return snapshot.docs.map((document) => document.data());
    } catch (error) {
      if (cancellationSignal?.aborted) {
        throw createFileUploadCancellationError(FileUploadStage.ReadProcessedText, cancellationSignal.reason);
      }
      if (attempt >= 3 || !isTransientProcessingTextReadError(error)) throw error;
      addSentryDiagnosticBreadcrumb({
        category: "document-upload",
        message: "Retrying a transient processed-text read",
        data: { taskId, nextAttempt: attempt + 1, maximumAttempts: 3 },
      });
      await new Promise<void>((resolve) => setTimeout(resolve, 750 * attempt));
    }
  }
}
